import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  RoomAudioRenderer,
  StartAudio,
  useLocalParticipant,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import {
  ConnectionState,
  type LocalTrackPublication,
  ParticipantEvent,
  RoomEvent,
  Track,
} from 'livekit-client';
import {
  ChevronDown,
  Hand,
  LayoutDashboard,
  MessageCircle,
  Mic,
  MicOff,
  PhoneOff,
  Pin,
  PinOff,
  ScreenShare,
  ScreenShareOff,
  SmilePlus,
  Video,
  VideoOff,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import InterviewMeetingChatPanel from '@/views/onboardingInterviews/components/InterviewMeetingChatPanel';
import type { OnboardingInterviewChatMessage } from '@/views/onboardingInterviews/services/onboardingInterviewService';
import type {
  MeetingChatConfig,
  ParticipantModerationAction,
  VideoLabels,
} from './types';
import { playMeetingSound } from './meetingSounds';
import MicrophoneGainProcessor from './MicrophoneGainProcessor';
import DraggableFloatingPreview from './DraggableFloatingPreview';
import EmptyVideoState from './EmptyVideoState';
import MeetingDesktopTutorial from './MeetingDesktopTutorial';
import ParticipantCameraTile from './ParticipantCameraTile';
import {
  participantHasRaisedHand,
  participantMetadataWithRaisedHand,
} from './participantHandRaise';
import { canPublishSource } from './participantPermissions';
import VideoFrame from './VideoFrame';
import VideoStage from './VideoStage';

const participantTileClassName = [
  'h-full w-full bg-slate-200 [&_[data-lk-video-container]]:h-full [&_[data-lk-video-container]]:w-full [&_video]:h-full [&_video]:w-full [&_video]:object-cover',
  '[&_.lk-participant-metadata]:!bottom-3 [&_.lk-participant-metadata]:!left-3 [&_.lk-participant-metadata]:!z-20',
  '[&_.lk-participant-metadata-item]:!min-w-0 [&_.lk-participant-metadata-item]:!max-w-[calc(100%_-_1.5rem)] [&_.lk-participant-metadata-item]:!gap-2 [&_.lk-participant-metadata-item]:!rounded-md [&_.lk-participant-metadata-item]:!border [&_.lk-participant-metadata-item]:!border-white/45 [&_.lk-participant-metadata-item]:!bg-black/80 [&_.lk-participant-metadata-item]:!px-2.5 [&_.lk-participant-metadata-item]:!py-1.5 [&_.lk-participant-metadata-item]:!font-semibold [&_.lk-participant-metadata-item]:!text-white [&_.lk-participant-metadata-item]:!shadow-[0_8px_22px_rgba(0,0,0,0.45)] [&_.lk-participant-metadata-item]:!backdrop-blur-sm',
  '[&_.lk-participant-metadata-item.lk-local-participant-metadata-item]:!border-white/90 [&_.lk-participant-metadata-item.lk-local-participant-metadata-item]:!bg-white/95 [&_.lk-participant-metadata-item.lk-local-participant-metadata-item]:!px-2.5 [&_.lk-participant-metadata-item.lk-local-participant-metadata-item]:!py-1.5 [&_.lk-participant-metadata-item.lk-local-participant-metadata-item]:!text-slate-950 [&_.lk-participant-metadata-item.lk-local-participant-metadata-item]:!shadow-[0_10px_24px_rgba(15,23,42,0.28)]',
  '[&_.lk-participant-name]:!text-white',
].join(' ');

const defaultDeviceValue = 'default-device';
const meetingReactionTopic = 'geoescala-meeting-reaction';
const meetingReactionDurationMs = 2800;
const meetingReactionCooldownMs = 600;
const meetingReactionOptions = ['👍', '👏', '🎉'];

type MeetingViewMode = 'auto' | 'grid' | 'focus';

const meetingViewOptions: Array<{
  mode: MeetingViewMode;
  label: string;
  description: string;
}> = [
  {
    mode: 'auto',
    label: 'Auto',
    description: 'Foca apresentação quando existir.',
  },
  {
    mode: 'grid',
    label: 'Grade',
    description: 'Mostra telas e câmeras juntas.',
  },
  {
    mode: 'focus',
    label: 'Foco',
    description: 'Deixa a apresentação ou quem fala em destaque.',
  },
];

type ParticipantPermissions = {
  canPublish?: boolean;
  canPublishSources?: unknown[];
};

type MeetingReaction = {
  id: string;
  emoji: string;
  participantName: string;
  x: number;
  xSoft: number;
  xEnd: number;
  delay: number;
  size: number;
};

type ChatMessagePopup = {
  message: OnboardingInterviewChatMessage;
  closing: boolean;
};

const localParticipantPermissions = (participant: unknown) =>
  (participant as { permissions?: ParticipantPermissions } | undefined)
    ?.permissions;

const roomMetadataHasActiveRecording = (metadata?: string) => {
  if (!metadata) return false;
  try {
    const parsed = JSON.parse(metadata) as {
      geoescala_recording_active?: unknown;
    };
    return parsed.geoescala_recording_active === true;
  } catch {
    return false;
  }
};

const shouldShowControlsByDefault = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(max-width: 767px)').matches ||
    window.matchMedia('(hover: none)').matches ||
    window.matchMedia('(pointer: coarse)').matches
  );
};

const LiveKitVideoLayout = ({
  videoLabels,
  hideParticipantMetadata,
  fillAvailableHeight = false,
  onClose,
  onModerateParticipant,
  moderationPending = false,
  recordingMode = false,
  initialDeviceIds,
  initialVideoEnabled = true,
  initialMicrophoneSensitivity = 100,
  microphoneGainProcessor,
  internalControls,
  chatConfig,
}: {
  videoLabels: VideoLabels;
  hideParticipantMetadata: boolean;
  fillAvailableHeight?: boolean;
  onClose?: () => void;
  onModerateParticipant?: (
    participantIdentity: string,
    action: ParticipantModerationAction,
    reason?: string,
  ) => void;
  moderationPending?: boolean;
  recordingMode?: boolean;
  initialVideoEnabled?: boolean;
  initialMicrophoneSensitivity?: number;
  microphoneGainProcessor: MicrophoneGainProcessor;
  internalControls?: ReactNode;
  chatConfig?: MeetingChatConfig;
  initialDeviceIds?: {
    audioInputDeviceId?: string;
    videoInputDeviceId?: string;
    audioOutputDeviceId?: string;
  };
}) => {
  const { t } = useTranslation();
  const room = useRoomContext();
  const {
    localParticipant,
    isMicrophoneEnabled,
    isCameraEnabled,
    isScreenShareEnabled,
  } = useLocalParticipant();
  const [mediaError, setMediaError] = useState('');
  const [controlsHovered, setControlsHovered] = useState(false);
  const [controlsPinned, setControlsPinned] = useState(false);
  const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
  const [videoSettingsOpen, setVideoSettingsOpen] = useState(false);
  const [controlsDefaultVisible, setControlsDefaultVisible] = useState(
    shouldShowControlsByDefault,
  );
  const [recordingActive, setRecordingActive] = useState(() =>
    roomMetadataHasActiveRecording(room.metadata),
  );
  const [connectionState, setConnectionState] = useState(room.state);
  const [readyFallbackElapsed, setReadyFallbackElapsed] = useState(false);
  const [handRaised, setHandRaised] = useState(() =>
    participantHasRaisedHand(localParticipant.metadata),
  );
  const [screenShareAllowed, setScreenShareAllowed] = useState(() =>
    canPublishSource(
      localParticipantPermissions(localParticipant),
      Track.Source.ScreenShare,
    ),
  );
  const [meetingViewMode, setMeetingViewMode] =
    useState<MeetingViewMode>('auto');
  const [stageModeChanging, setStageModeChanging] = useState(false);
  const [meetingReactions, setMeetingReactions] = useState<MeetingReaction[]>(
    [],
  );
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [chatMessagePopups, setChatMessagePopups] = useState<
    ChatMessagePopup[]
  >([]);
  const [activeSpeakerIdentities, setActiveSpeakerIdentities] = useState<
    string[]
  >(() =>
    room.activeSpeakers
      .filter(
        (participant) =>
          !participant.isLocal && !participant.identity.startsWith('recorder-'),
      )
      .map((participant) => participant.identity),
  );
  const [moderationLocks, setModerationLocks] = useState({
    microphone: false,
    camera: false,
  });
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioInputDeviceId, setAudioInputDeviceId] = useState(
    initialDeviceIds?.audioInputDeviceId || '',
  );
  const [videoInputDeviceId, setVideoInputDeviceId] = useState(
    initialDeviceIds?.videoInputDeviceId || '',
  );
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState(
    initialDeviceIds?.audioOutputDeviceId || '',
  );
  const [microphoneTestActive, setMicrophoneTestActive] = useState(false);
  const [microphoneTestLevel, setMicrophoneTestLevel] = useState(0);
  const [microphoneSensitivity, setMicrophoneSensitivity] = useState(
    initialMicrophoneSensitivity,
  );
  const [microphoneGainStatus, setMicrophoneGainStatus] = useState<
    'pending' | 'active' | 'unavailable'
  >('pending');
  const microphoneSensitivityRef = useRef(initialMicrophoneSensitivity);
  microphoneSensitivityRef.current = microphoneSensitivity;
  const localMuteIntentRef = useRef<Partial<Record<Track.Source, boolean>>>({});
  const lastReactionSentAtRef = useRef(0);
  const microphoneTestCleanupRef = useRef<(() => void) | null>(null);
  const reactionTimeoutsRef = useRef<number[]>([]);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  const chatPopupTimeoutsRef = useRef(new Map<number, number[]>());
  const stageModeChangeTimeoutRef = useRef<number | null>(null);
  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]);

  const attachMicrophoneGainProcessor = useCallback(
    async (publication?: LocalTrackPublication) => {
      const audioTrack = publication?.audioTrack;
      if (
        publication?.source !== Track.Source.Microphone ||
        !audioTrack
      ) {
        return;
      }
      if (audioTrack.getProcessor() === microphoneGainProcessor) {
        setMicrophoneGainStatus('active');
        return;
      }

      try {
        await audioTrack.setProcessor(microphoneGainProcessor);
        setMicrophoneGainStatus('active');
      } catch {
        setMicrophoneGainStatus('unavailable');
        // Keep the original microphone track when Web Audio processing is unavailable.
      }
    },
    [microphoneGainProcessor],
  );

  useEffect(() => {
    const handleLocalTrackPublished = (publication: LocalTrackPublication) => {
      void attachMicrophoneGainProcessor(publication);
    };

    void attachMicrophoneGainProcessor(
      localParticipant.getTrackPublication(Track.Source.Microphone),
    );
    localParticipant.on(
      ParticipantEvent.LocalTrackPublished,
      handleLocalTrackPublished,
    );
    return () => {
      localParticipant.off(
        ParticipantEvent.LocalTrackPublished,
        handleLocalTrackPublished,
      );
    };
  }, [attachMicrophoneGainProcessor, localParticipant]);

  useEffect(() => {
    const panel = chatPanelRef.current;
    if (!panel) return;
    if (chatOpen) {
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('inert', '');
    }
  }, [chatOpen]);

  const dismissChatMessagePopup = useCallback((messageId: number) => {
    chatPopupTimeoutsRef.current.get(messageId)?.forEach(window.clearTimeout);
    chatPopupTimeoutsRef.current.delete(messageId);
    setChatMessagePopups((current) =>
      current.filter((popup) => popup.message.id !== messageId),
    );
  }, []);

  const clearChatMessagePopups = useCallback(() => {
    chatPopupTimeoutsRef.current.forEach((timeouts) =>
      timeouts.forEach(window.clearTimeout),
    );
    chatPopupTimeoutsRef.current.clear();
    setChatMessagePopups([]);
  }, []);

  const showChatMessagePopup = useCallback(
    (message: OnboardingInterviewChatMessage) => {
      chatPopupTimeoutsRef.current.get(message.id)?.forEach(window.clearTimeout);
      playMeetingSound('chat');
      setChatMessagePopups((current) => [
        ...current.filter((popup) => popup.message.id !== message.id),
        { message, closing: false },
      ].slice(-3));

      const closeTimeout = window.setTimeout(() => {
        setChatMessagePopups((current) =>
          current.map((popup) =>
            popup.message.id === message.id
              ? { ...popup, closing: true }
              : popup,
          ),
        );
      }, 5200);
      const removeTimeout = window.setTimeout(
        () => dismissChatMessagePopup(message.id),
        5500,
      );
      chatPopupTimeoutsRef.current.set(message.id, [
        closeTimeout,
        removeTimeout,
      ]);
    },
    [dismissChatMessagePopup],
  );

  useEffect(() => {
    if (chatOpen) clearChatMessagePopups();
  }, [chatOpen, clearChatMessagePopups]);

  useEffect(
    () => () => {
      chatPopupTimeoutsRef.current.forEach((timeouts) =>
        timeouts.forEach(window.clearTimeout),
      );
    },
    [],
  );
  const visibleTracks = useMemo(
    () =>
      tracks.filter(
        (trackRef) =>
          !(
            'participant' in trackRef &&
            trackRef.participant?.identity?.startsWith('recorder-')
          ),
      ),
    [tracks],
  );
  const audioInputDevices = useMemo(
    () => devices.filter((device) => device.kind === 'audioinput'),
    [devices],
  );
  const videoInputDevices = useMemo(
    () => devices.filter((device) => device.kind === 'videoinput'),
    [devices],
  );
  const audioOutputDevices = useMemo(
    () => devices.filter((device) => device.kind === 'audiooutput'),
    [devices],
  );
  const canSelectAudioOutput =
    typeof HTMLMediaElement !== 'undefined' &&
    'setSinkId' in HTMLMediaElement.prototype;
  const selectClassName =
    'h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none focus:border-ring disabled:cursor-not-allowed disabled:opacity-60';
  const deviceOptionLabel = (
    device: MediaDeviceInfo,
    fallbackKey: string,
    index: number,
  ) => device.label || `${t(fallbackKey)} ${index + 1}`;
  const resolveDeviceSelection = (value: string) =>
    value === defaultDeviceValue ? '' : value;

  const refreshDevices = async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    setDevices(await navigator.mediaDevices.enumerateDevices());
  };

  const playAudioTest = async () => {
    try {
      setMediaError('');
      const AudioContextConstructor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioContextConstructor) {
        throw new Error('audio context unavailable');
      }
      const audioContext = new AudioContextConstructor();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const destination = audioContext.createMediaStreamDestination();
      const audio = new Audio();
      audio.srcObject = destination.stream;
      const setSinkId = (audio as HTMLAudioElement & {
        setSinkId?: (sinkId: string) => Promise<void>;
      }).setSinkId;
      if (audioOutputDeviceId && setSinkId) {
        await setSinkId.call(audio, audioOutputDeviceId);
      }

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(720, audioContext.currentTime);
      gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(
        0.16,
        audioContext.currentTime + 0.04,
      );
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        audioContext.currentTime + 0.55,
      );
      oscillator.connect(gain);
      gain.connect(destination);
      oscillator.start();
      await audio.play();
      window.setTimeout(() => {
        oscillator.stop();
        audio.pause();
        destination.stream.getTracks().forEach((track) => track.stop());
        void audioContext.close();
      }, 650);
    } catch {
      setMediaError(t('onboarding_interviews.meeting.audio_test_error'));
    }
  };

  const stopMicrophoneTest = () => {
    microphoneTestCleanupRef.current?.();
    microphoneTestCleanupRef.current = null;
    setMicrophoneTestActive(false);
    setMicrophoneTestLevel(0);
  };

  const testMicrophone = async () => {
    stopMicrophoneTest();
    try {
      setMediaError('');
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('media devices unavailable');
      }
      const AudioContextConstructor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioContextConstructor) {
        throw new Error('audio context unavailable');
      }
      const testStream = await navigator.mediaDevices.getUserMedia({
        audio: audioInputDeviceId
          ? {
              autoGainControl: false,
              deviceId: { exact: audioInputDeviceId },
            }
          : { autoGainControl: false },
        video: false,
      });
      const audioContext = new AudioContextConstructor();
      const source = audioContext.createMediaStreamSource(testStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);
      let animationFrame = 0;
      const updateLevel = () => {
        analyser.getByteTimeDomainData(samples);
        const sum = samples.reduce((total, sample) => {
          const normalized = (sample - 128) / 128;
          return total + normalized * normalized;
        }, 0);
        const rms = Math.sqrt(sum / samples.length);
        setMicrophoneTestLevel(
          Math.min(
            100,
            Math.round(
              rms * 260 * (microphoneSensitivityRef.current / 100),
            ),
          ),
        );
        animationFrame = window.requestAnimationFrame(updateLevel);
      };
      const timeout = window.setTimeout(stopMicrophoneTest, 5000);
      microphoneTestCleanupRef.current = () => {
        window.cancelAnimationFrame(animationFrame);
        window.clearTimeout(timeout);
        source.disconnect();
        testStream.getTracks().forEach((track) => track.stop());
        void audioContext.close();
      };
      setMicrophoneTestActive(true);
      updateLevel();
    } catch {
      setMediaError(t('onboarding_interviews.meeting.microphone_test_error'));
      stopMicrophoneTest();
    }
  };

  const changeAudioInputDevice = async (deviceId: string) => {
    const previousDeviceId = audioInputDeviceId;
    if (isMicrophoneEnabled && moderationLocks.microphone) {
      setMediaError('Você foi silenciado pelo entrevistador.');
      return;
    }
    setAudioInputDeviceId(deviceId);
    if (!isMicrophoneEnabled) return;
    try {
      setMediaError('');
      await room.switchActiveDevice(
        'audioinput',
        deviceId || 'default',
        Boolean(deviceId),
      );
      void refreshDevices().catch(() => undefined);
    } catch {
      setAudioInputDeviceId(previousDeviceId);
      setMediaError('Não foi possível alterar o microfone.');
    }
  };

  const changeVideoInputDevice = async (deviceId: string) => {
    setVideoInputDeviceId(deviceId);
    if (!isCameraEnabled) return;
    if (moderationLocks.camera) {
      setMediaError('Sua câmera foi ocultada pelo entrevistador.');
      return;
    }
    try {
      setMediaError('');
      await localParticipant.setCameraEnabled(
        true,
        deviceId ? { deviceId } : undefined,
      );
      void refreshDevices().catch(() => undefined);
    } catch {
      setMediaError('Não foi possível alterar a câmera.');
    }
  };

  const toggleMicrophone = async () => {
    try {
      setMediaError('');
      const shouldEnableMicrophone = !isMicrophoneEnabled;
      if (shouldEnableMicrophone && moderationLocks.microphone) {
        setMediaError('Você foi silenciado pelo entrevistador.');
        return;
      }
      if (!shouldEnableMicrophone) {
        localMuteIntentRef.current[Track.Source.Microphone] = true;
      } else {
        delete localMuteIntentRef.current[Track.Source.Microphone];
      }
      await localParticipant.setMicrophoneEnabled(
        shouldEnableMicrophone,
        shouldEnableMicrophone
          ? {
              autoGainControl: false,
              ...(audioInputDeviceId
                ? { deviceId: audioInputDeviceId }
                : {}),
            }
          : undefined,
      );
      playMeetingSound(shouldEnableMicrophone ? 'enable' : 'disable');
    } catch {
      delete localMuteIntentRef.current[Track.Source.Microphone];
      setMediaError('Não foi possível alterar o microfone.');
    }
  };

  const toggleCamera = async () => {
    try {
      setMediaError('');
      const shouldEnableCamera = !isCameraEnabled;
      if (shouldEnableCamera && moderationLocks.camera) {
        setMediaError('Sua câmera foi ocultada pelo entrevistador.');
        return;
      }
      if (!shouldEnableCamera) {
        localMuteIntentRef.current[Track.Source.Camera] = true;
      } else {
        delete localMuteIntentRef.current[Track.Source.Camera];
      }
      await localParticipant.setCameraEnabled(
        shouldEnableCamera,
        videoInputDeviceId ? { deviceId: videoInputDeviceId } : undefined,
      );
      playMeetingSound(shouldEnableCamera ? 'enable' : 'disable');
    } catch {
      delete localMuteIntentRef.current[Track.Source.Camera];
      setMediaError('Não foi possível alterar a câmera.');
    }
  };

  const stopActiveScreenShares = (exceptIdentity?: string) => {
    const participantIdentities = Array.from(
      new Set(
        screenShareTracks
          .map((trackRef) => trackRef.participant.identity)
          .filter((identity) => identity && identity !== exceptIdentity),
      ),
    );
    participantIdentities.forEach((identity) => {
      if (identity === localParticipant.identity) {
        void localParticipant.setScreenShareEnabled(false).catch(() => undefined);
        return;
      }
      if (!onModerateParticipant || !identity.startsWith('candidate-')) {
        return;
      }
      onModerateParticipant(identity, 'disable_screen_share');
    });
  };

  const toggleScreenShare = async () => {
    try {
      setMediaError('');
      const shouldEnableScreenShare = !isScreenShareEnabled;
      if (shouldEnableScreenShare && !screenShareAllowed) {
        setMediaError('Somente o entrevistador pode compartilhar a tela.');
        return;
      }
      if (shouldEnableScreenShare) {
        stopActiveScreenShares(localParticipant.identity);
      }
      await localParticipant.setScreenShareEnabled(shouldEnableScreenShare);
      playMeetingSound(
        shouldEnableScreenShare ? 'screen_share' : 'screen_unshare',
      );
    } catch {
      setMediaError('Não foi possível alterar o compartilhamento de tela.');
    }
  };

  const toggleHandRaise = async () => {
    const shouldRaiseHand = !handRaised;
    try {
      setMediaError('');
      await localParticipant.setMetadata(
        participantMetadataWithRaisedHand(
          localParticipant.metadata,
          shouldRaiseHand,
        ),
      );
      setHandRaised(shouldRaiseHand);
      playMeetingSound(shouldRaiseHand ? 'hand_raise' : 'hand_lower');
    } catch {
      setHandRaised(participantHasRaisedHand(localParticipant.metadata));
      setMediaError('Não foi possível alterar a mão levantada.');
    }
  };

  const createMeetingReactionId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const addMeetingReaction = (
    emoji: string,
    participantName: string,
    id = createMeetingReactionId(),
  ) => {
    const reaction: MeetingReaction = {
      id,
      emoji,
      participantName,
      x: Math.round((Math.random() - 0.5) * 260),
      xSoft: 0,
      xEnd: 0,
      delay: Math.round(Math.random() * 160),
      size: 42 + Math.round(Math.random() * 14),
    };
    reaction.xSoft = Math.round(reaction.x * 0.2);
    reaction.xEnd = Math.round(reaction.x * 1.35);
    setMeetingReactions((current) => [...current.slice(-18), reaction]);
    const timeout = window.setTimeout(() => {
      setMeetingReactions((current) =>
        current.filter((item) => item.id !== reaction.id),
      );
      reactionTimeoutsRef.current = reactionTimeoutsRef.current.filter(
        (item) => item !== timeout,
      );
    }, meetingReactionDurationMs + reaction.delay + 120);
    reactionTimeoutsRef.current.push(timeout);
  };

  const sendMeetingReaction = async (emoji: string) => {
    const now = Date.now();
    if (now - lastReactionSentAtRef.current < meetingReactionCooldownMs) return;
    lastReactionSentAtRef.current = now;
    const reactionId = createMeetingReactionId();
    const participantName =
      localParticipant.name || localParticipant.identity || 'Participante';

    addMeetingReaction(emoji, participantName, reactionId);
    playMeetingSound('reaction');

    try {
      await localParticipant.publishData(
        new TextEncoder().encode(
          JSON.stringify({
            type: 'meeting_reaction',
            id: reactionId,
            emoji,
            participantName,
          }),
        ),
        { reliable: false, topic: meetingReactionTopic },
      );
    } catch {
      setMediaError('Não foi possível enviar a reação.');
    }
  };

  const handleCloseCall = () => {
    playMeetingSound('leave');
    window.setTimeout(() => onClose?.(), 180);
  };

  const changeMeetingViewMode = (mode: MeetingViewMode) => {
    if (mode === meetingViewMode) return;
    if (stageModeChangeTimeoutRef.current) {
      window.clearTimeout(stageModeChangeTimeoutRef.current);
    }
    setStageModeChanging(true);
    setMeetingViewMode(mode);
    stageModeChangeTimeoutRef.current = window.setTimeout(() => {
      setStageModeChanging(false);
      stageModeChangeTimeoutRef.current = null;
    }, 180);
  };

  const localTrack = useMemo(
    () =>
      recordingMode
        ? undefined
        : visibleTracks.find(
            (trackRef) =>
              'participant' in trackRef &&
              trackRef.participant?.isLocal &&
              trackRef.source === Track.Source.Camera,
          ),
    [recordingMode, visibleTracks],
  );

  const remoteTracks = useMemo(
    () =>
      visibleTracks.filter(
        (trackRef) =>
          'participant' in trackRef &&
          (recordingMode || !trackRef.participant?.isLocal) &&
          trackRef.source === Track.Source.Camera,
      ),
    [recordingMode, visibleTracks],
  );
  const screenShareTracks = useMemo(
    () =>
      visibleTracks.filter(
        (trackRef) =>
          'participant' in trackRef &&
          trackRef.source === Track.Source.ScreenShare,
      ),
    [visibleTracks],
  );
  const sortedRemoteTracks = useMemo(
    () =>
      [...remoteTracks].sort((left, right) => {
        const leftSpeakingIndex = activeSpeakerIdentities.indexOf(
          left.participant.identity,
        );
        const rightSpeakingIndex = activeSpeakerIdentities.indexOf(
          right.participant.identity,
        );
        if (leftSpeakingIndex === rightSpeakingIndex) return 0;
        if (leftSpeakingIndex === -1) return 1;
        if (rightSpeakingIndex === -1) return -1;
        return leftSpeakingIndex - rightSpeakingIndex;
      }),
    [activeSpeakerIdentities, remoteTracks],
  );
  const focusedScreenShareTrack = screenShareTracks[0];
  const resolvedParticipantTileClassName = [
    participantTileClassName,
    hideParticipantMetadata ? '[&_.lk-participant-metadata]:hidden' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const controlsVisible =
    controlsDefaultVisible || controlsPinned || controlsHovered;
  const remoteGridClassName = (count: number) =>
    [
      'grid h-full min-h-0 grid-cols-1 gap-1',
      count > 1 ? 'md:grid-cols-2' : '',
      count > 4 ? 'xl:grid-cols-3' : '',
      count > 9 ? '2xl:grid-cols-4' : '',
    ]
      .filter(Boolean)
      .join(' ');
  const renderRemoteCameraTile = (
    trackRef: (typeof remoteTracks)[number],
    className = '',
  ) => (
    <ParticipantCameraTile
      key={`${trackRef.participant.identity}-${trackRef.source}`}
      trackRef={trackRef}
      className={[resolvedParticipantTileClassName, className]
        .filter(Boolean)
        .join(' ')}
      onModerateParticipant={onModerateParticipant}
      moderationPending={moderationPending}
    />
  );
  const renderScreenShareTile = (
    trackRef: (typeof screenShareTracks)[number],
    className = '',
  ) => (
    <ParticipantCameraTile
      key={`${trackRef.participant.identity}-${trackRef.source}`}
      trackRef={trackRef}
      className={[resolvedParticipantTileClassName, className]
        .filter(Boolean)
        .join(' ')}
    />
  );
  const renderRemoteCameraGrid = (items: typeof remoteTracks) =>
    items.length > 0 ? (
      <div className={remoteGridClassName(items.length)}>
        {items.map((trackRef) => renderRemoteCameraTile(trackRef))}
      </div>
    ) : (
      <EmptyVideoState
        label={
          t('teleattendance.waiting_remote_participant') ||
          'Aguardando participante remoto...'
        }
      />
    );
  const renderCameraFilmstrip = (items: typeof remoteTracks) =>
    items.length > 0 ? (
      <div className="flex h-[clamp(104px,22vh,168px)] min-h-0 gap-1 overflow-x-auto rounded-lg bg-black/35 p-1 lg:h-full lg:w-[clamp(176px,18vw,260px)] lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto">
        {items.map((trackRef) => (
          <div
            key={`${trackRef.participant.identity}-${trackRef.source}-filmstrip`}
            className="h-full min-w-[clamp(136px,30vw,220px)] overflow-hidden rounded-md bg-black lg:h-[clamp(112px,24%,170px)] lg:min-h-[112px] lg:w-full lg:min-w-0"
          >
            {renderRemoteCameraTile(trackRef)}
          </div>
        ))}
      </div>
    ) : null;
  const renderCameraFocusStage = (items: typeof remoteTracks) => {
    const mainTrack = items[0];
    if (!mainTrack) return renderRemoteCameraGrid(items);
    const sideTracks = items.slice(1);
    return sideTracks.length > 0 ? (
      <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)_auto] gap-1 lg:grid-cols-[minmax(0,1fr)_clamp(176px,18vw,260px)] lg:grid-rows-1">
        <div className="min-h-0 overflow-hidden rounded-lg bg-black">
          {renderRemoteCameraTile(mainTrack)}
        </div>
        {renderCameraFilmstrip(sideTracks)}
      </div>
    ) : (
      renderRemoteCameraTile(mainTrack)
    );
  };
  const renderMeetStyleStage = () => {
    if (meetingViewMode === 'grid') {
      const gridItemsCount = screenShareTracks.length + sortedRemoteTracks.length;
      if (gridItemsCount > 0) {
        return (
          <div className={remoteGridClassName(gridItemsCount)}>
            {screenShareTracks.map((trackRef) => renderScreenShareTile(trackRef))}
            {sortedRemoteTracks.map((trackRef) =>
              renderRemoteCameraTile(trackRef),
            )}
          </div>
        );
      }
    }
    if (focusedScreenShareTrack) {
      return (
        <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)_auto] gap-1 lg:grid-cols-[minmax(0,1fr)_clamp(176px,18vw,260px)] lg:grid-rows-1">
          <div className="min-h-0 overflow-hidden rounded-lg bg-black">
            {renderScreenShareTile(focusedScreenShareTrack)}
          </div>
          {renderCameraFilmstrip(sortedRemoteTracks)}
        </div>
      );
    }
    if (meetingViewMode === 'focus' && sortedRemoteTracks.length > 0) {
      return renderCameraFocusStage(sortedRemoteTracks);
    }
    if (sortedRemoteTracks.length > 0) {
      return renderRemoteCameraGrid(sortedRemoteTracks);
    }
    return (
      <EmptyVideoState
        label={
          t('teleattendance.waiting_remote_participant') ||
          'Aguardando participante remoto...'
        }
      />
    );
  };
  const localCameraTrackReady = Boolean(localTrack?.publication?.track);
  const meetingReady =
    recordingMode ||
    (connectionState === ConnectionState.Connected &&
      (!initialVideoEnabled ||
        localCameraTrackReady ||
        readyFallbackElapsed ||
        Boolean(mediaError)));

  useEffect(() => {
    if (controlsVisible) return;
    setAudioSettingsOpen(false);
    setVideoSettingsOpen(false);
    stopMicrophoneTest();
  }, [controlsVisible]);

  useEffect(() => {
    const handleActiveSpeakersChanged = (speakers: typeof room.activeSpeakers) => {
      setActiveSpeakerIdentities(
        speakers
          .filter(
            (participant) =>
              !participant.isLocal &&
              !participant.identity.startsWith('recorder-'),
          )
          .map((participant) => participant.identity),
      );
    };
    handleActiveSpeakersChanged(room.activeSpeakers);
    room.on(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);
    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);
    };
  }, [room]);

  useEffect(() => {
    const handleMeetingReaction = (
      payload: Uint8Array,
      participant?: {
        identity?: string;
        name?: string;
        isLocal?: boolean;
      },
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== meetingReactionTopic || participant?.isLocal) return;
      try {
        const reaction = JSON.parse(new TextDecoder().decode(payload)) as {
          type?: string;
          id?: string;
          emoji?: string;
          participantName?: string;
        };
        if (
          reaction.type !== 'meeting_reaction' ||
          !reaction.emoji ||
          !meetingReactionOptions.includes(reaction.emoji)
        ) {
          return;
        }
        addMeetingReaction(
          reaction.emoji,
          reaction.participantName ||
            participant?.name ||
            participant?.identity ||
            'Participante',
          reaction.id,
        );
        playMeetingSound('reaction');
      } catch {
        // Ignore malformed reaction packets from the room.
      }
    };
    room.on(RoomEvent.DataReceived, handleMeetingReaction);
    return () => {
      room.off(RoomEvent.DataReceived, handleMeetingReaction);
    };
  }, [room]);

  useEffect(() => {
    if (!isScreenShareEnabled) return;
    const otherPresentationActive = screenShareTracks.some(
      (trackRef) => !trackRef.participant.isLocal,
    );
    if (!otherPresentationActive) return;
    void localParticipant.setScreenShareEnabled(false).catch(() => undefined);
  }, [isScreenShareEnabled, localParticipant, screenShareTracks]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const queries = [
      window.matchMedia('(max-width: 767px)'),
      window.matchMedia('(hover: none)'),
      window.matchMedia('(pointer: coarse)'),
    ];
    const updateControlsVisibility = () => {
      setControlsDefaultVisible(queries.some((query) => query.matches));
    };
    updateControlsVisibility();
    queries.forEach((query) => {
      query.addEventListener('change', updateControlsVisibility);
    });
    return () => {
      queries.forEach((query) => {
        query.removeEventListener('change', updateControlsVisibility);
      });
    };
  }, []);

  useEffect(() => {
    void refreshDevices().catch(() => undefined);
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.addEventListener) return undefined;
    const handleDeviceChange = () => {
      void refreshDevices().catch(() => undefined);
    };
    mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  useEffect(
    () => () => {
      stopMicrophoneTest();
      reactionTimeoutsRef.current.forEach((timeout) => {
        window.clearTimeout(timeout);
      });
      reactionTimeoutsRef.current = [];
      if (stageModeChangeTimeoutRef.current) {
        window.clearTimeout(stageModeChangeTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    setAudioInputDeviceId((currentDeviceId) =>
      currentDeviceId &&
      !audioInputDevices.some((device) => device.deviceId === currentDeviceId)
        ? ''
        : currentDeviceId,
    );
    setVideoInputDeviceId((currentDeviceId) =>
      currentDeviceId &&
      !videoInputDevices.some((device) => device.deviceId === currentDeviceId)
        ? ''
        : currentDeviceId,
    );
    setAudioOutputDeviceId((currentDeviceId) =>
      currentDeviceId &&
      !audioOutputDevices.some((device) => device.deviceId === currentDeviceId)
        ? ''
        : currentDeviceId,
    );
  }, [audioInputDevices, audioOutputDevices, videoInputDevices]);

  useEffect(() => {
    const outputDeviceId = audioOutputDeviceId;
    if (!outputDeviceId || typeof document === 'undefined') return;
    document.querySelectorAll('audio').forEach((audio) => {
      const setSinkId = (audio as HTMLAudioElement & {
        setSinkId?: (sinkId: string) => Promise<void>;
      }).setSinkId;
      if (setSinkId) void setSinkId.call(audio, outputDeviceId);
    });
  }, [audioOutputDeviceId, visibleTracks]);

  useEffect(() => {
    if (recordingMode) return;
    void refreshDevices().catch(() => undefined);
  }, [recordingMode]);

  useEffect(() => {
    const handleConnectionStateChanged = (state: ConnectionState) => {
      setConnectionState(state);
    };
    setConnectionState(room.state);
    room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
    return () => {
      room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
    };
  }, [room]);

  useEffect(() => {
    setReadyFallbackElapsed(false);
    if (
      recordingMode ||
      connectionState !== ConnectionState.Connected ||
      !initialVideoEnabled ||
      localCameraTrackReady
    ) {
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      setReadyFallbackElapsed(true);
    }, 3500);
    return () => window.clearTimeout(timeout);
  }, [
    connectionState,
    initialVideoEnabled,
    localCameraTrackReady,
    recordingMode,
  ]);

  useEffect(() => {
    const handleRoomMetadataChanged = (metadata?: string) => {
      setRecordingActive(roomMetadataHasActiveRecording(metadata));
    };
    setRecordingActive(roomMetadataHasActiveRecording(room.metadata));
    room.on(RoomEvent.RoomMetadataChanged, handleRoomMetadataChanged);
    return () => {
      room.off(RoomEvent.RoomMetadataChanged, handleRoomMetadataChanged);
    };
  }, [room]);

  useEffect(() => {
    const handleParticipantMetadataChanged = (
      metadata: string | undefined,
      participant: typeof localParticipant,
    ) => {
      if (participant.isLocal) {
        setHandRaised(participantHasRaisedHand(metadata));
      }
    };
    setHandRaised(participantHasRaisedHand(localParticipant.metadata));
    room.on(
      RoomEvent.ParticipantMetadataChanged,
      handleParticipantMetadataChanged,
    );
    return () => {
      room.off(
        RoomEvent.ParticipantMetadataChanged,
        handleParticipantMetadataChanged,
      );
    };
  }, [localParticipant, room]);

  useEffect(() => {
    const handleTrackMuted = (publication: { source?: Track.Source }) => {
      if (publication.source === Track.Source.Microphone) {
        if (localMuteIntentRef.current[Track.Source.Microphone]) {
          delete localMuteIntentRef.current[Track.Source.Microphone];
          return;
        }
        setModerationLocks((locks) => ({ ...locks, microphone: true }));
        setMediaError('Você foi silenciado pelo entrevistador.');
        return;
      }
      if (publication.source === Track.Source.Camera) {
        if (localMuteIntentRef.current[Track.Source.Camera]) {
          delete localMuteIntentRef.current[Track.Source.Camera];
          return;
        }
        setModerationLocks((locks) => ({ ...locks, camera: true }));
        setMediaError('Sua câmera foi ocultada pelo entrevistador.');
        return;
      }
      if (publication.source === Track.Source.ScreenShare) {
        setScreenShareAllowed(false);
        setMediaError('Sua apresentação foi encerrada pelo entrevistador.');
      }
    };

    const handleTrackUnmuted = (publication: { source?: Track.Source }) => {
      if (publication.source === Track.Source.Microphone) {
        setModerationLocks((locks) => ({ ...locks, microphone: false }));
        return;
      }
      if (publication.source === Track.Source.Camera) {
        setModerationLocks((locks) => ({ ...locks, camera: false }));
        return;
      }
      if (publication.source === Track.Source.ScreenShare) {
        setScreenShareAllowed(true);
      }
    };
    const handlePermissionsChanged = () => {
      const permissions = localParticipantPermissions(localParticipant);
      const canPublishMicrophone = canPublishSource(
        permissions,
        Track.Source.Microphone,
      );
      const canPublishCamera = canPublishSource(
        permissions,
        Track.Source.Camera,
      );
      const canPublishScreenShare = canPublishSource(
        permissions,
        Track.Source.ScreenShare,
      );
      setScreenShareAllowed(canPublishScreenShare);
      if (!canPublishScreenShare && isScreenShareEnabled) {
        void localParticipant.setScreenShareEnabled(false).catch(() => undefined);
      }
      setModerationLocks({
        microphone: !canPublishMicrophone,
        camera: !canPublishCamera,
      });
      if (!canPublishMicrophone) {
        setMediaError('Você foi silenciado pelo entrevistador.');
        return;
      }
      if (!canPublishCamera) {
        setMediaError('Sua câmera foi ocultada pelo entrevistador.');
        return;
      }
      if (canPublishMicrophone && canPublishCamera) {
        setMediaError('');
      }
    };

    handlePermissionsChanged();
    localParticipant.on(ParticipantEvent.TrackMuted, handleTrackMuted);
    localParticipant.on(ParticipantEvent.TrackUnmuted, handleTrackUnmuted);
    localParticipant.on(
      ParticipantEvent.ParticipantPermissionsChanged,
      handlePermissionsChanged,
    );

    return () => {
      localParticipant.off(ParticipantEvent.TrackMuted, handleTrackMuted);
      localParticipant.off(ParticipantEvent.TrackUnmuted, handleTrackUnmuted);
      localParticipant.off(
        ParticipantEvent.ParticipantPermissionsChanged,
        handlePermissionsChanged,
      );
    };
  }, [localParticipant]);

  return (
    <div
      className={[
        fillAvailableHeight
          ? 'relative h-full min-h-0 rounded-lg border border-border/70 bg-[#17191d] p-2 shadow-[0_16px_40px_rgba(15,23,42,0.16)] sm:p-3'
          : 'relative min-h-[60vh] rounded-[28px] border border-[#cfe2ff] bg-[linear-gradient(180deg,_rgba(255,255,255,0.98),_rgba(248,250,252,0.98))] p-4 shadow-[0_24px_60px_rgba(59,130,246,0.12)] sm:p-5',
        'overflow-hidden transition-[padding] ease-out',
        chatConfig && chatOpen ? 'md:pr-[392px]' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ transitionDuration: '220ms' }}
    >
      <style>
        {`
          @keyframes geoescala-meeting-reaction-float {
            0% {
              opacity: 0;
              transform: translate3d(0, 22px, 0) scale(0.72) rotate(-4deg);
            }
            12% {
              opacity: 1;
              transform: translate3d(var(--reaction-x-soft), -18px, 0) scale(1) rotate(2deg);
            }
            68% {
              opacity: 1;
              transform: translate3d(var(--reaction-x), -42vh, 0) scale(1.18) rotate(-3deg);
            }
            100% {
              opacity: 0;
              transform: translate3d(var(--reaction-x-end), -62vh, 0) scale(0.92) rotate(5deg);
            }
          }

          .geoescala-meeting-reaction {
            animation: geoescala-meeting-reaction-float ${meetingReactionDurationMs}ms cubic-bezier(0.2, 0.78, 0.2, 1) forwards;
            animation-delay: var(--reaction-delay);
          }

          @keyframes geoescala-chat-popup-in {
            from { opacity: 0; transform: translate3d(24px, 0, 0); }
            to { opacity: 1; transform: translate3d(0, 0, 0); }
          }

          @keyframes geoescala-chat-popup-out {
            from { opacity: 1; transform: translate3d(0, 0, 0); }
            to { opacity: 0; transform: translate3d(24px, 0, 0); }
          }

          .geoescala-chat-popup {
            animation: geoescala-chat-popup-in 220ms ease-out both;
          }

          .geoescala-chat-popup-closing {
            animation: geoescala-chat-popup-out 180ms ease-in both;
          }

          @media (prefers-reduced-motion: reduce) {
            .geoescala-chat-popup,
            .geoescala-chat-popup-closing {
              animation-duration: 1ms;
            }
          }
        `}
      </style>
      <RoomAudioRenderer />
      {!recordingMode ? (
        <>
          <StartAudio
            label="Ativar áudio da reunião"
            className="absolute left-1/2 top-5 z-30 -translate-x-1/2 rounded-full border border-white/25 bg-black/75 px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(0,0,0,0.38)] backdrop-blur-md hover:bg-black/85"
          />
          <MeetingDesktopTutorial />
        </>
      ) : null}

      {recordingActive ? (
        <div className="absolute left-1/2 top-16 z-30 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 rounded-md border border-red-200/70 bg-red-600 px-4 py-2 text-center text-sm font-semibold text-white shadow-[0_12px_28px_rgba(127,29,29,0.35)]">
          Esta reunião está sendo gravada.
        </div>
      ) : null}

      {meetingReactions.length > 0 ? (
        <div
          className="pointer-events-none absolute inset-x-5 bottom-28 top-16 z-30 overflow-hidden"
          aria-hidden="true"
        >
          {meetingReactions.map((reaction) => (
            <span
              key={reaction.id}
              className="geoescala-meeting-reaction absolute bottom-0 left-[calc(50%-4.5rem)] flex w-36 flex-col items-center justify-center bg-transparent drop-shadow-[0_10px_18px_rgba(0,0,0,0.38)]"
              style={
                {
                  '--reaction-x': `${reaction.x}px`,
                  '--reaction-x-soft': `${reaction.xSoft}px`,
                  '--reaction-x-end': `${reaction.xEnd}px`,
                  '--reaction-delay': `${reaction.delay}ms`,
                } as CSSProperties
              }
              title={reaction.participantName}
            >
              <span
                className="flex h-16 w-16 items-center justify-center"
                style={{ fontSize: `${reaction.size}px` }}
              >
                {reaction.emoji}
              </span>
              <span className="mt-0.5 max-w-full truncate text-center text-xs font-semibold leading-tight text-white [text-shadow:_0_1px_5px_rgba(0,0,0,0.9)]">
                {reaction.participantName}
              </span>
            </span>
          ))}
        </div>
      ) : null}

      <VideoStage
        label={videoLabels.main}
        className={
          fillAvailableHeight
            ? 'flex h-full min-h-0 flex-col [&>div:last-child]:min-h-0 [&>div:last-child]:flex-1'
            : 'h-full'
        }
      >
        <VideoFrame
          className={
            fillAvailableHeight
              ? 'min-h-[360px] flex-1 rounded-lg border-white/10 bg-black'
              : 'h-[58vh] min-h-[320px]'
          }
        >
          <div
            className={[
              'h-full min-h-0 transition-[opacity,transform] duration-200 ease-out will-change-transform',
              stageModeChanging
                ? 'scale-[0.998] opacity-95'
                : 'scale-100 opacity-100',
            ].join(' ')}
          >
            {renderMeetStyleStage()}
          </div>
        </VideoFrame>
      </VideoStage>

      {!meetingReady ? (
        <div className="absolute inset-2 z-40 flex items-center justify-center rounded-lg bg-black/80 text-white backdrop-blur-sm transition-opacity duration-200 sm:inset-3">
          <div className="flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold shadow-[0_16px_42px_rgba(0,0,0,0.34)]">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            {t('onboarding_interviews.meeting.connecting') || 'Conectando...'}
          </div>
        </div>
      ) : null}

      {!recordingMode ? (
        <DraggableFloatingPreview
          className="w-[clamp(132px,24vw,270px)]"
          defaultPlacementClassName={
            fillAvailableHeight
              ? 'bottom-5 right-3 sm:bottom-6 sm:right-6'
              : 'bottom-7 right-3 sm:bottom-8 sm:right-8'
          }
        >
          <VideoStage label={videoLabels.preview} hideLabel>
            <VideoFrame className="aspect-[4/3]">
              {localTrack?.publication?.track ? (
                <ParticipantCameraTile
                  trackRef={localTrack}
                  className={resolvedParticipantTileClassName}
                />
              ) : (
                <EmptyVideoState
                  label={
                    t('teleattendance.local_camera_placeholder') ||
                    'Sua câmera aparecerá aqui'
                  }
                />
              )}
            </VideoFrame>
          </VideoStage>
        </DraggableFloatingPreview>
      ) : null}

      {!recordingMode ? (
        <div
          className={[
            'absolute bottom-0 left-0 right-0 z-20 h-32 transition-[right] ease-out',
            chatConfig && chatOpen ? 'md:right-[392px]' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ transitionDuration: '220ms' }}
          onMouseEnter={() => setControlsHovered(true)}
          onMouseLeave={() => setControlsHovered(false)}
        >
          <div
            className={[
              'absolute left-1/2 flex max-w-[calc(100%-1rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-2 rounded-full border border-white/20 bg-black/45 px-2 py-2 shadow-[0_14px_32px_rgba(0,0,0,0.34)] backdrop-blur-md transition-all duration-200 ease-out focus-within:pointer-events-auto focus-within:translate-y-0 focus-within:opacity-100 sm:gap-3 sm:px-3',
              fillAvailableHeight ? 'bottom-6' : 'bottom-8',
              controlsVisible
                ? 'pointer-events-auto translate-y-0 opacity-100'
                : 'pointer-events-none translate-y-4 opacity-0',
            ].join(' ')}
          >
            <div className="relative">
              <Button
                type="button"
                size="icon"
                variant="outline"
                className={[
                  isMicrophoneEnabled
                    ? 'h-12 w-12 rounded-full border-border bg-background/95 shadow-lg'
                    : 'h-12 w-12 rounded-full border-red-600 bg-red-600 text-white shadow-lg hover:bg-red-700 hover:text-white',
                  moderationLocks.microphone
                    ? 'cursor-not-allowed opacity-80'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={
                  moderationLocks.microphone
                    ? 'Microfone bloqueado pelo entrevistador'
                    : isMicrophoneEnabled
                      ? 'Desativar microfone'
                      : 'Ativar microfone'
                }
                title={
                  moderationLocks.microphone
                    ? 'Microfone bloqueado pelo entrevistador'
                    : isMicrophoneEnabled
                      ? 'Desativar microfone'
                      : 'Ativar microfone'
                }
                onClick={() => void toggleMicrophone()}
              >
                {isMicrophoneEnabled ? (
                  <Mic size={21} />
                ) : (
                  <MicOff size={21} />
                )}
              </Button>
              <DropdownMenu
                open={audioSettingsOpen}
                onOpenChange={setAudioSettingsOpen}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border border-white/30 bg-background text-foreground shadow-md hover:bg-accent hover:text-accent-foreground"
                    aria-label={t(
                      'onboarding_interviews.meeting.audio_settings',
                    )}
                    title={t('onboarding_interviews.meeting.audio_settings')}
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  align="center"
                  className="w-80 p-3"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="grid gap-3">
                    <p className="text-sm font-semibold text-popover-foreground">
                      {t('onboarding_interviews.meeting.audio_settings')}
                    </p>
                    <label className="grid gap-1.5">
                      <span className="text-xs font-medium text-muted-foreground">
                        {t('onboarding_interviews.meeting.microphone_input')}
                      </span>
                      <select
                        value={audioInputDeviceId || defaultDeviceValue}
                        onChange={(event) =>
                          void changeAudioInputDevice(
                            resolveDeviceSelection(event.target.value),
                          )
                        }
                        className={selectClassName}
                      >
                        <option value={defaultDeviceValue}>
                          {t('onboarding_interviews.meeting.default_device')}
                        </option>
                        {audioInputDevices.map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {deviceOptionLabel(
                              device,
                              'onboarding_interviews.meeting.microphone_input',
                              index,
                            )}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="grid gap-2">
                      <span className="flex items-center justify-between gap-3 text-xs font-medium text-muted-foreground">
                        <span>
                          {t(
                            'onboarding_interviews.meeting.microphone_sensitivity',
                          )}
                        </span>
                        <span className="flex items-center gap-1.5 tabular-nums text-foreground">
                          {microphoneSensitivity}%
                          <span
                            className={
                              microphoneGainStatus === 'active'
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : microphoneGainStatus === 'unavailable'
                                  ? 'text-amber-700 dark:text-amber-400'
                                  : 'text-muted-foreground'
                            }
                          >
                            ·{' '}
                            {t(
                              microphoneGainStatus === 'active'
                                ? 'onboarding_interviews.meeting.microphone_gain_active'
                                : microphoneGainStatus === 'unavailable'
                                  ? 'onboarding_interviews.meeting.microphone_gain_unavailable'
                                  : 'onboarding_interviews.meeting.microphone_gain_pending',
                            )}
                          </span>
                        </span>
                      </span>
                      <Slider
                        min={25}
                        max={400}
                        step={5}
                        value={[microphoneSensitivity]}
                        onValueChange={([value]) => {
                          setMicrophoneSensitivity(value);
                          microphoneGainProcessor.setGain(value / 100);
                          void attachMicrophoneGainProcessor(
                            localParticipant.getTrackPublication(
                              Track.Source.Microphone,
                            ),
                          );
                        }}
                        aria-label={t(
                          'onboarding_interviews.meeting.microphone_sensitivity',
                        )}
                      />
                    </label>
                    <label className="grid gap-1.5">
                      <span className="text-xs font-medium text-muted-foreground">
                        {t('onboarding_interviews.meeting.speaker_output')}
                      </span>
                      <select
                        value={audioOutputDeviceId || defaultDeviceValue}
                        onChange={(event) =>
                          setAudioOutputDeviceId(
                            resolveDeviceSelection(event.target.value),
                          )
                        }
                        className={selectClassName}
                        disabled={
                          !canSelectAudioOutput ||
                          audioOutputDevices.length === 0
                        }
                      >
                        <option value={defaultDeviceValue}>
                          {t('onboarding_interviews.meeting.default_device')}
                        </option>
                        {audioOutputDevices.map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {deviceOptionLabel(
                              device,
                              'onboarding_interviews.meeting.speaker_output',
                              index,
                            )}
                          </option>
                        ))}
                      </select>
                    </label>
                    {!canSelectAudioOutput ? (
                      <p className="text-xs text-muted-foreground">
                        {t(
                          'onboarding_interviews.meeting.audio_output_not_supported',
                        )}
                      </p>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 rounded-md"
                      onClick={() => void playAudioTest()}
                    >
                      {t('onboarding_interviews.meeting.test_audio')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 rounded-md"
                      onClick={() => void testMicrophone()}
                    >
                      {t('onboarding_interviews.meeting.test_microphone')}
                    </Button>
                    {microphoneTestActive ? (
                      <div className="grid gap-1.5">
                        <div className="h-2 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-emerald-500 transition-[width] duration-75"
                            style={{ width: `${microphoneTestLevel}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            'onboarding_interviews.meeting.microphone_test_hint',
                          )}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="relative">
              <Button
                type="button"
                size="icon"
                variant="outline"
                className={[
                  isCameraEnabled
                    ? 'h-12 w-12 rounded-full border-border bg-background/95 shadow-lg'
                    : 'h-12 w-12 rounded-full border-red-600 bg-red-600 text-white shadow-lg hover:bg-red-700 hover:text-white',
                  moderationLocks.camera ? 'cursor-not-allowed opacity-80' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={
                  moderationLocks.camera
                    ? 'Câmera bloqueada pelo entrevistador'
                    : isCameraEnabled
                      ? 'Desativar câmera'
                      : 'Ativar câmera'
                }
                title={
                  moderationLocks.camera
                    ? 'Câmera bloqueada pelo entrevistador'
                    : isCameraEnabled
                      ? 'Desativar câmera'
                      : 'Ativar câmera'
                }
                onClick={() => void toggleCamera()}
              >
                {isCameraEnabled ? <Video size={21} /> : <VideoOff size={21} />}
              </Button>
              <DropdownMenu
                open={videoSettingsOpen}
                onOpenChange={setVideoSettingsOpen}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border border-white/30 bg-background text-foreground shadow-md hover:bg-accent hover:text-accent-foreground"
                    aria-label={t(
                      'onboarding_interviews.meeting.video_settings',
                    )}
                    title={t('onboarding_interviews.meeting.video_settings')}
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  align="center"
                  className="w-80 p-3"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="grid gap-3">
                    <p className="text-sm font-semibold text-popover-foreground">
                      {t('onboarding_interviews.meeting.video_settings')}
                    </p>
                    <label className="grid gap-1.5">
                      <span className="text-xs font-medium text-muted-foreground">
                        {t('onboarding_interviews.meeting.camera_input')}
                      </span>
                      <select
                        value={videoInputDeviceId || defaultDeviceValue}
                        onChange={(event) =>
                          void changeVideoInputDevice(
                            resolveDeviceSelection(event.target.value),
                          )
                        }
                        className={selectClassName}
                      >
                        <option value={defaultDeviceValue}>
                          {t('onboarding_interviews.meeting.default_device')}
                        </option>
                        {videoInputDevices.map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {deviceOptionLabel(
                              device,
                              'onboarding_interviews.meeting.camera_input',
                              index,
                            )}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {screenShareAllowed || isScreenShareEnabled ? (
              <Button
                type="button"
                size="icon"
                variant="outline"
                className={
                  isScreenShareEnabled
                    ? 'h-12 w-12 rounded-full border-emerald-600 bg-emerald-600 text-white shadow-lg hover:bg-emerald-700 hover:text-white'
                    : 'h-12 w-12 rounded-full border-border bg-background/95 shadow-lg'
                }
                aria-label={
                  isScreenShareEnabled
                    ? 'Parar compartilhamento de tela'
                    : 'Compartilhar tela'
                }
                title={
                  isScreenShareEnabled
                    ? 'Parar compartilhamento de tela'
                    : 'Compartilhar tela'
                }
                onClick={() => void toggleScreenShare()}
              >
                {isScreenShareEnabled ? (
                  <ScreenShareOff size={21} />
                ) : (
                  <ScreenShare size={21} />
                )}
              </Button>
            ) : null}
            <Button
              type="button"
              size="icon"
              variant="outline"
              className={
                handRaised
                  ? 'h-12 w-12 rounded-full border-amber-500 bg-amber-400 text-amber-950 shadow-lg hover:bg-amber-500 hover:text-amber-950'
                  : 'h-12 w-12 rounded-full border-border bg-background/95 shadow-lg'
              }
              aria-label={handRaised ? 'Abaixar mão' : 'Levantar mão'}
              title={handRaised ? 'Abaixar mão' : 'Levantar mão'}
              aria-pressed={handRaised}
              onClick={() => void toggleHandRaise()}
            >
              <Hand size={23} strokeWidth={2.35} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-12 w-12 rounded-full border-border bg-background/95 shadow-lg hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800 dark:hover:border-white/30 dark:hover:bg-white/10 dark:hover:text-white"
                  aria-label="Enviar reação"
                  title="Enviar reação"
                >
                  <SmilePlus size={21} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="center"
                className="rounded-full p-1.5"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center gap-1">
                  {meetingReactionOptions.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-full text-2xl transition-transform hover:scale-110 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-400 dark:hover:bg-white/10"
                      aria-label={`Enviar reação ${emoji}`}
                      title={`Enviar reação ${emoji}`}
                      onClick={() => void sendMeetingReaction(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            {chatConfig ? (
              <Button
                type="button"
                size="icon"
                variant="outline"
                className={[
                  'relative h-12 w-12 rounded-full border-border bg-background/95 shadow-lg hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800 dark:hover:border-white/30 dark:hover:bg-white/10 dark:hover:text-white',
                  chatOpen
                    ? 'border-sky-600 bg-sky-600 text-white hover:bg-sky-700 hover:text-white'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={t('onboarding_interviews.chat.open')}
                title={t('onboarding_interviews.chat.open')}
                aria-pressed={chatOpen}
                onClick={() => setChatOpen((current) => !current)}
              >
                <MessageCircle size={21} />
                {chatUnread > 0 ? (
                  <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white ring-2 ring-background">
                    {chatUnread > 99 ? '99+' : chatUnread}
                  </span>
                ) : null}
              </Button>
            ) : null}
            {remoteTracks.length > 0 || screenShareTracks.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className={[
                      'h-12 w-12 rounded-full border-border bg-background/95 shadow-lg hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800 dark:hover:border-white/30 dark:hover:bg-white/10 dark:hover:text-white',
                      meetingViewMode !== 'auto'
                        ? 'border-sky-600 bg-sky-600 text-white hover:bg-sky-700 hover:text-white'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-label="Organizar visualização"
                    title="Organizar visualização"
                  >
                    <LayoutDashboard size={21} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  align="center"
                  className="w-64 p-2"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="grid gap-1">
                    {meetingViewOptions.map((option) => {
                      const selected = meetingViewMode === option.mode;
                      return (
                        <button
                          key={option.mode}
                          type="button"
                          className={[
                            'group flex items-center gap-3 rounded-md px-3 py-2 text-left transition-colors',
                            selected
                              ? 'bg-sky-600 text-white shadow-sm'
                              : 'text-slate-700 hover:bg-sky-50 hover:text-sky-950 dark:text-slate-100 dark:hover:bg-white/10 dark:hover:text-white',
                          ].join(' ')}
                          aria-pressed={selected}
                          onClick={() => changeMeetingViewMode(option.mode)}
                        >
                          <span
                            className={[
                              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                              selected
                                ? 'bg-white/20 text-white'
                                : 'bg-slate-100 text-slate-600 group-hover:bg-sky-100 group-hover:text-sky-700 dark:bg-white/10 dark:text-slate-200 dark:group-hover:bg-white/15 dark:group-hover:text-white',
                            ].join(' ')}
                          >
                            {option.mode === 'grid' ? (
                              <Video className="h-4 w-4" />
                            ) : option.mode === 'focus' ? (
                              <ScreenShare className="h-4 w-4" />
                            ) : (
                              <LayoutDashboard className="h-4 w-4" />
                            )}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold">
                              {option.label}
                            </span>
                            <span
                              className={[
                                'block text-xs leading-snug',
                                selected
                                  ? 'text-white/78'
                                  : 'text-slate-500 group-hover:text-sky-700 dark:text-slate-400 dark:group-hover:text-slate-200',
                              ].join(' ')}
                            >
                              {option.description}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {!controlsDefaultVisible ? (
              <Button
                type="button"
                size="icon"
                variant="outline"
                className={
                  controlsPinned
                    ? 'h-12 w-12 rounded-full border-primary bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 hover:text-primary-foreground'
                    : 'h-12 w-12 rounded-full border-border bg-background/95 shadow-lg'
                }
                aria-label={
                  controlsPinned ? 'Desafixar controles' : 'Fixar controles'
                }
                title={
                  controlsPinned ? 'Desafixar controles' : 'Fixar controles'
                }
                aria-pressed={controlsPinned}
                onClick={() => setControlsPinned((pinned) => !pinned)}
              >
                {controlsPinned ? <PinOff size={21} /> : <Pin size={21} />}
              </Button>
            ) : null}
            {internalControls}
            {onClose ? (
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="h-12 w-12 rounded-full bg-red-600 text-white shadow-lg hover:bg-red-700"
                aria-label="Sair da chamada"
                title="Sair da chamada"
                onClick={handleCloseCall}
              >
                <PhoneOff size={21} />
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {mediaError ? (
        <div className="absolute bottom-24 left-1/2 z-20 -translate-x-1/2 rounded-md bg-destructive px-3 py-2 text-xs text-destructive-foreground shadow-lg">
          {mediaError}
        </div>
      ) : null}
      {!chatOpen && chatMessagePopups.length ? (
        <div
          className="pointer-events-none absolute right-3 top-3 z-40 grid w-[min(360px,calc(100%_-_1.5rem))] gap-2"
          aria-live="polite"
          aria-label={t('onboarding_interviews.chat.title')}
        >
          {chatMessagePopups.map(({ message, closing }) => {
            const preview =
              message.content.trim() ||
              t('onboarding_interviews.chat.attachment_notification');
            return (
              <div
                key={message.id}
                className={[
                  'geoescala-chat-popup pointer-events-auto relative flex overflow-hidden rounded-md border border-border bg-background/95 text-foreground shadow-xl backdrop-blur-sm',
                  closing ? 'geoescala-chat-popup-closing' : '',
                ].join(' ')}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 px-3 py-3 pr-1 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  onClick={() => setChatOpen(true)}
                  aria-label={t('onboarding_interviews.chat.open')}
                >
                  <span className="mb-1 flex items-center gap-2 text-sm font-semibold">
                    <MessageCircle className="h-4 w-4 shrink-0 text-sky-600" />
                    <span className="truncate">{message.sender_name}</span>
                  </span>
                  <span className="line-clamp-2 block break-words text-xs leading-relaxed text-muted-foreground">
                    {preview}
                  </span>
                </button>
                <button
                  type="button"
                  className="m-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => dismissChatMessagePopup(message.id)}
                  aria-label={t('common.close')}
                  title={t('common.close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
      {chatConfig ? (
        <div
          ref={chatPanelRef}
          className={[
            'absolute inset-0 z-50 transform-gpu border-l border-border bg-background shadow-[-16px_0_34px_rgba(0,0,0,0.22)] transition-[transform,opacity] ease-out md:inset-y-2 md:left-auto md:right-2 md:w-[380px] md:rounded-md md:border',
            chatOpen
              ? 'translate-x-0 opacity-100'
              : 'pointer-events-none translate-x-full opacity-0',
          ].join(' ')}
          style={{ transitionDuration: '220ms' }}
          aria-hidden={!chatOpen}
        >
          <InterviewMeetingChatPanel
            access={chatConfig}
            room={room}
            open={chatOpen}
            currentParticipantIdentity={localParticipant.identity}
            onClose={() => setChatOpen(false)}
            onIncomingMessage={showChatMessagePopup}
            onUnreadChange={setChatUnread}
          />
        </div>
      ) : null}
    </div>
  );
};

export default LiveKitVideoLayout;
